import { useRouter } from 'expo-router';
import { ChevronLeft, ShieldCheck } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { ScrollView, Pressable, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { goBackOrTo } from '@/lib/nav';

/**
 * Privacy policy (Wave 48 — Play Store launch requirement). This route
 * doubles as the public policy URL for the store listing:
 * https://homestead.expo.app/privacy
 *
 * Keep this page HONEST and in sync with reality. The whole policy
 * follows from the local-first architecture: there are no accounts and
 * no server-side database, so there is almost nothing to disclose.
 * If cloud sync / accounts ship, this page MUST be rewritten first.
 */

const EFFECTIVE = 'September 7, 2026';
const CONTACT = 'support@smartsiteservices.com';

export default function PrivacyPolicy() {
  const router = useRouter();
  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/account')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Privacy policy</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.heroRow}>
          <View style={styles.heroIcon}>
            <ShieldCheck size={20} color={Palette.forest} strokeWidth={2} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.h1}>Your home&apos;s data stays home.</Text>
            <Text style={styles.effective}>Effective {EFFECTIVE}</Text>
          </View>
        </View>

        <Text style={styles.lede}>
          Homestead is a local-first app. Everything you enter — your home
          profile, tasks, systems, expenses, quotes, contacts, notes,
          photos, and documents — is stored in a database on your own
          device. There are no user accounts, we run no server that
          stores your data, and we cannot see what you put in the app.
        </Text>

        <Section title="What we collect">
          <Para>
            Nothing, by default. Homestead does not require an account,
            an email address, or a name. All app data lives on your
            device and leaves it only in the specific cases below, each
            triggered by you.
          </Para>
        </Section>

        <Section title="Location & address">
          <Para>
            Your ZIP code (typed or derived from device location, if you
            grant permission) is used on-device to pick a climate zone
            for your maintenance plan. If you enter a street address, it
            is sent to Google&apos;s Places and Static Maps services —
            through our proxy, so no key or identity of yours is
            attached — to power address autocomplete and the map preview.
            We do not store your address or location on any server.
          </Para>
        </Section>

        <Section title="Document scanning (AI scanner)">
          <Para>
            When you scan a receipt, invoice, estimate, or business
            card, the photo is sent to Google Cloud Vision for text
            recognition, routed through our server-side proxy. The image
            is processed transiently to extract text and is not stored
            by us. The photo itself is saved only on your device, with
            the rest of your files.
          </Para>
        </Section>

        <Section title="Notifications">
          <Para>
            Reminders and digests are scheduled locally on your device
            using its notification system. No push-notification server
            is involved, and notification content never leaves your
            phone.
          </Para>
        </Section>

        <Section title="Backups & exports">
          <Para>
            Backups, CSV exports, PDF records, and file downloads are
            created on your device and go only where you choose to save
            or share them. We never receive a copy.
          </Para>
        </Section>

        <Section title="What we never do">
          <Para>
            We do not sell, rent, or share your data. We do not run
            third-party advertising or ad tracking. We do not build
            profiles of you. We cannot access your data even if asked —
            we don&apos;t have it.
          </Para>
        </Section>

        <Section title="Support">
          <Para>
            If you email support or use the in-app contact form, we
            receive what you send (your message and, if you include it,
            your name and email) and use it only to respond.
          </Para>
        </Section>

        <Section title="Deleting your data">
          <Para>
            Uninstalling the app removes all of its data from your
            device. You can also wipe everything from inside the app via
            Settings → Reset. Because we hold no copy, there is nothing
            for us to delete on our side.
          </Para>
        </Section>

        <Section title="Children">
          <Para>
            Homestead is a home-maintenance tool intended for adults and
            is not directed at children under 13.
          </Para>
        </Section>

        <Section title="Changes">
          <Para>
            If a future feature changes any of this — for example
            optional accounts and cloud backup — we will update this
            policy first and note the change in the app.
          </Para>
        </Section>

        <Section title="Contact">
          <Para>
            Questions about privacy: {CONTACT}. Homestead is made by
            Smart Site Services, Michigan, USA.
          </Para>
        </Section>

        <Text style={styles.footer}>
          homestead.expo.app/privacy · Effective {EFFECTIVE}
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function Para({ children }: { children: ReactNode }) {
  return <Text style={styles.para}>{children}</Text>;
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  content: {
    paddingHorizontal: 22,
    paddingBottom: 60,
    maxWidth: 720,
    width: '100%',
    alignSelf: 'center',
  },
  heroRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    marginTop: Spacing.three,
  },
  heroIcon: {
    width: 42, height: 42, borderRadius: 13,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  h1: {
    fontFamily: Fonts.serif, fontSize: 24, color: Palette.ink,
    letterSpacing: -0.3, lineHeight: 29,
  },
  effective: {
    marginTop: 2,
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkFaint,
    letterSpacing: 0.4,
  },
  lede: {
    marginTop: Spacing.four,
    fontFamily: Fonts.sansMedium, fontSize: 14.5, lineHeight: 22,
    color: Palette.ink,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.lg,
    padding: 16,
  },
  section: { marginTop: Spacing.five },
  sectionTitle: {
    fontFamily: Fonts.serifBold, fontSize: 17, color: Palette.ink,
    letterSpacing: -0.2, marginBottom: 6,
  },
  para: {
    fontFamily: Fonts.sansMedium, fontSize: 13.5, lineHeight: 21,
    color: Palette.inkSoft,
  },
  footer: {
    marginTop: Spacing.six,
    textAlign: 'center',
    fontFamily: Fonts.sansSemibold, fontSize: 12, color: Palette.inkFaint,
    letterSpacing: 0.4,
  },
});
